import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { PlatformToggleCounts } from '../../types/gotypes'
import type { LabelQuery } from '../../components/social-media/types'

export interface UseSkySourceCountsResult {
  /** Undefined until it loads — the Source row then draws no counts at all. */
  counts: PlatformToggleCounts | undefined
  isUpdating: boolean
}

/**
 * What each button of the Source row is worth (posts and comments per business
 * source) under the page's CURRENT filters — useSkyPlatformCounts for the row
 * below it, and keyed the same way for the same reason: a count on a button is
 * what pressing it gives you now. The source predicate is stripped
 * server-side, so the page's own query is sent as is.
 */
export function useSkySourceCounts(
  product: string,
  labelQuery?: LabelQuery,
  enabled = true,
): UseSkySourceCountsResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'source-counts', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<PlatformToggleCounts>(queryKey),
    queryFn: async ({ signal }): Promise<PlatformToggleCounts> => {
      const res = await axios.post<PlatformToggleCounts>(
        skyProductURL(product, '/entities/source-counts'),
        { labelQuery },
        { signal },
      )
      return res.data
    },
  })

  return { counts: query.data, isUpdating: query.isPlaceholderData }
}

export default useSkySourceCounts
